import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Phone,
  BookOpen,
  School,
  School2,
  GraduationCap,
  Omega,
  Star,
  Users,
  BriefcaseBusiness,
  Handshake,
  MapPin,
  Globe,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Clock,
  HelpCircle,
  Book,
} from 'lucide-react';

import SEOHead from './SEOHead';
import BookingPopup from './BookingPopup';
import ReviewsCarousel from './GoogleReviews';

import ProfilePic from '../profilepic.webp';
import bgImage from '../new-background.webp';
import Logo from '../assets/logo.webp';

const FAQItem = ({ question, answer, isOpen, onClick, icon: Icon }) => (
  <div className="border-b border-teal-400/30 last:border-0">
    <button
      className={`w-full py-6 flex justify-between items-center text-left transition-colors duration-300 ${
        isOpen ? 'bg-teal-700/30' : 'hover:bg-teal-700/10'
      }`}
      onClick={onClick}
    >
      <div className="flex items-center space-x-4 pr-8">
        <div className="flex-shrink-0 w-10 flex justify-center">
          <Icon className="h-6 w-6 text-yellow-300" />
        </div>
        <h3 className="text-lg font-semibold text-white">{question}</h3>
      </div>
      {isOpen ? (
        <ChevronUp className="h-5 w-5 text-yellow-300 flex-shrink-0 mr-4" />
      ) : (
        <ChevronDown className="h-5 w-5 text-yellow-300 flex-shrink-0 mr-4" />
      )}
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
      <p className="text-white/90 px-20 py-6 italic leading-relaxed bg-teal-700/20">{answer}</p>
    </div>
  </div>
);

const genericFAQs = [
  {
    icon: HelpCircle,
    question: "Why is career counselling essential in today's world?",
    answer:
      "In today's rapidly evolving job market, career counselling has become crucial. With over 3000+ career options available, making an informed decision can be overwhelming. Professional career counselling helps you understand your strengths, interests, and personality traits, matching them with suitable career paths.",
  },
  {
    icon: Users,
    question: "Isn't career guidance from parents and teachers enough?",
    answer:
      "While parents and teachers provide valuable advice, professional career counselling offers unbiased, up-to-date industry knowledge. Career counsellors have access to the latest market trends, detailed information about emerging careers, and specialized tools to assess your aptitude accurately.",
  },
  {
    icon: Clock,
    question: 'When is the right time to seek career counselling?',
    answer:
      'Career counselling is beneficial at various life stages: during school when choosing subjects (8th–10th grade), before college admissions, during graduation, or when considering a career switch. The best time is when you are approaching a major career or educational decision point.',
  },
  {
    icon: Book,
    question: 'How does your career counselling process work?',
    answer:
      'Our process begins with a FREE initial consultation to understand your needs. We then conduct comprehensive psychometric assessments to evaluate your interests, aptitudes, and personality traits. Based on the results, we provide detailed career mapping and a personalized roadmap for your career development.',
  },
  {
    icon: MessageCircle,
    question: 'How do I schedule a counselling session?',
    answer:
      "Click the 'Book Your Free Session' button on this page, call us at 9731166822, or email sudutta@gmail.com. We offer 100% online counselling for clients across Bangalore, Jamshedpur, and Ranchi.",
  },
];

const CityLandingPage = ({ cityConfig }) => {
  const {
    city,
    canonicalUrl,
    title,
    description,
    keywords,
    heroSubtitle,
    localHighlight,
    localFAQs = [],
    ctaNote,
    showRemoteBanner = false,
  } = cityConfig;

  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFAQIndex, setOpenFAQIndex] = useState(0);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const allFAQs = [...localFAQs, ...genericFAQs];

  return (
    <>
      <SEOHead
        title={title}
        description={description}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
        city={city}
      />

      <div className="min-h-screen bg-gray-50 font-lato">
        {/* Header */}
        <header
          className={`fixed w-full top-0 z-50 transition-all duration-300 font-lato ${
            isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
          }`}
        >
          <div className="w-full px-4 lg:px-12">
            <div className="max-w-[1920px] mx-auto">
              <div className="flex items-center h-20 lg:h-24">
                <div className="flex-shrink-0 lg:w-[15%] pl-0 lg:pl-8">
                  {isScrolled && (
                    <img
                      src={Logo}
                      alt="Nirvana Career Counselling Logo"
                      loading="lazy"
                      className="h-16 lg:h-20 object-contain mt-2"
                    />
                  )}
                </div>

                <div className="hidden lg:flex flex-grow justify-center items-center animate-pulseSlow">
                  <div
                    className={`inline-block px-8 text-black ml-16 lg:ml-24 text-lg font-bold rounded-md transition-all duration-300 transform ${
                      isScrolled ? 'bg-teal-600' : 'bg-white'
                    }`}
                  >
                    <p
                      className={`text-2xl font-bold font-lato text-center mb-2 mt-2 ${
                        isScrolled ? 'text-white' : 'text-teal-700'
                      }`}
                    >
                      Your First Session with Us is
                      <span className="ml-2 text-4xl font-bold inline-block text-red-600 drop-shadow-outline-white">
                        FREE!
                      </span>
                    </p>
                    <p
                      className={`text-sm font-normal text-center mb-2 ${
                        isScrolled ? 'text-yellow-200' : 'text-teal-600'
                      }`}
                    >
                      📍 Career Counselling in {city} — Online Sessions Available
                    </p>
                  </div>
                </div>

                <div className="hidden lg:flex lg:w-[22%] justify-end items-center pl-12">
                  <button
                    onClick={() => setIsBookingOpen(true)}
                    className={`px-6 py-2 rounded-md transition-all duration-300 ${
                      isScrolled
                        ? 'bg-teal-600 text-white font-bold hover:bg-yellow-500 hover:text-black'
                        : 'bg-white text-teal-700 font-bold hover:bg-amber-600 hover:text-white'
                    }`}
                  >
                    Contact Us
                  </button>
                </div>

                <div className="lg:hidden flex-grow mx-4 animate-pulseSoft">
                  <div
                    className={`text-center px-2 py-1 rounded ${
                      isScrolled ? 'bg-teal-600' : 'bg-white'
                    }`}
                  >
                    <p
                      className={`text-lg font-bold ${
                        isScrolled ? 'text-white' : 'text-teal-700'
                      }`}
                    >
                      First Session
                      <span className="ml-1 text-xl font-bold inline-block text-red-600 drop-shadow-outline-white">
                        FREE!
                      </span>
                    </p>
                    <p
                      className={`text-xs ${isScrolled ? 'text-yellow-200' : 'text-teal-600'}`}
                    >
                      {city} • Online
                    </p>
                  </div>
                </div>

                <div className="flex items-center">
                  <button
                    onClick={() => setIsBookingOpen(true)}
                    className={`lg:hidden p-2 rounded-md transition-all duration-300 ${
                      isScrolled ? 'bg-teal-600 text-white' : 'bg-white text-teal-600'
                    }`}
                  >
                    <Phone className="h-6 w-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <main role="main">
          <section
            aria-label="Hero Section"
            className="hero-parallax relative flex items-center"
            style={{
              backgroundImage: `linear-gradient(to right, rgba(8, 64, 59, 0.9), rgba(13, 148, 136, 0.8)), url(${bgImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              minHeight: '100vh',
            }}
          >
            <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:mt-12 lg:mt-24">
              <div className="relative z-10">
                <div className="text-center mb-8">
                  <img
                    src={Logo}
                    alt="Nirvana Career Counselling Logo"
                    className="mx-auto mb-8 w-40 h-40 lg:w-60 lg:h-60 object-contain"
                  />
                  <h1 className="text-3xl md:text-5xl font-lato font-black mb-4 lg:mb-6 text-white drop-shadow-lg px-2">
                    Career Counselling in {city}
                  </h1>
                  <h2 className="text-2xl md:text-3xl font-lato font-black max-w-6xl mx-auto text-yellow-400 drop-shadow-lg italic px-4">
                    {heroSubtitle}
                  </h2>
                </div>

                <div className="text-center">
                  <p className="text-lg md:text-2xl font-lato font-light mb-4 lg:mb-6 max-w-4xl mx-auto text-white drop-shadow-md px-4">
                    Expert Career Guidance for Students and Professionals
                  </p>
                  <p className="text-base md:text-xl font-lato font-medium mb-8 lg:mb-12 max-w-4xl mx-auto text-yellow-300 drop-shadow-md px-4">
                    🌐 {ctaNote}
                  </p>

                  <div className="space-y-4 md:space-y-0 md:space-x-6 mt-4 animate-pulseSlow px-4">
                    <button
                      onClick={() => setIsBookingOpen(true)}
                      className="w-full md:w-auto inline-block px-8 py-3 md:px-12 md:py-4 bg-yellow-500 text-black text-lg font-bold rounded-md hover:bg-amber-600 transition-all duration-300 hover:shadow-lg hover:text-white transform hover:-translate-y-0.5"
                    >
                      Book Your Free Session
                    </button>
                  </div>

                  <div className="mt-6 text-center">
                    <p className="text-white text-sm opacity-90">
                      ⭐ 4.9/5 Rating | 🎓 30+ Years Experience | 🔒 100% Confidential
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Remote Trust Banner */}
          {showRemoteBanner && (
            <section className="bg-teal-700 border-b border-teal-500 py-4 px-4 text-center">
              <p className="text-white text-base md:text-lg font-medium">
                🌐 Sessions are 100% online — attend from the comfort of your home in {city}. No commute, same expert guidance.
              </p>
            </section>
          )}

          {/* Local Highlight */}
          {localHighlight && (
            <section className="bg-teal-50 py-8 px-4">
              <div className="max-w-3xl mx-auto text-center">
                <p className="text-teal-800 text-lg leading-relaxed">{localHighlight}</p>
              </div>
            </section>
          )}

          {/* Psychometric Assessments */}
          <section aria-label="Psychometric Assessments" className="py-20 bg-white font-lato">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Psychometric Career Assessments</h2>
                <div className="w-24 h-1 bg-teal-600 mx-auto rounded-full mb-8"></div>
                <p className="text-gray-600 max-w-3xl mx-auto px-4">
                  Discover your perfect career path with our comprehensive assessment tools designed for students and
                  professionals at every stage.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
                {[
                  { icon: BookOpen, title: ['Career Analysis', 'Classes 2nd to 7th'], desc: "Discover your child's Multiple Intelligence profile and early aptitude indicators." },
                  { icon: School, title: ['Career Analysis', 'Classes 8th-10th'], desc: 'Find the most suitable career path and subject combinations for high school success.' },
                  { icon: School2, title: ['Career Analysis', 'Classes 11th-12th'], desc: 'Get a detailed career roadmap with college and course recommendations.' },
                  { icon: Omega, title: ['Career Analysis', 'Graduates'], desc: 'Plan your post-graduation career path with detailed insights and guidance.' },
                ].map(({ icon: Icon, title: cardTitle, desc }, i) => (
                  <div key={i} className="bg-white rounded-lg shadow-md p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl flex flex-col justify-between border-2 border-teal-300">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                        <Icon className="h-8 w-8 text-teal-600" />
                      </div>
                      <h4 className="text-xl font-semibold text-gray-800 mb-4 flex flex-col items-center">
                        {cardTitle.map((t) => <span key={t}>{t}</span>)}
                      </h4>
                      <p className="text-gray-600 mb-4 text-center">{desc}</p>
                    </div>
                    <button
                      onClick={() => setIsBookingOpen(true)}
                      className="mt-4 w-full px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-yellow-300 hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-teal-600"
                    >
                      Start Assessment
                    </button>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8 max-w-5xl mx-auto">
                {[
                  { icon: Handshake, title: 'Business Management', desc: 'Explore your potential in business and management careers.' },
                  { icon: GraduationCap, title: ['Career Analysis', 'Professionals'], desc: 'Get guidance for career transitions and professional growth opportunities.' },
                  { icon: BriefcaseBusiness, title: 'Engineering Assessment', desc: 'Choose the right engineering specialization based on your aptitude and interests.' },
                ].map(({ icon: Icon, title: cardTitle, desc }, i) => (
                  <div key={i} className="bg-white rounded-lg shadow-md p-6 transform transition duration-300 hover:scale-105 hover:shadow-xl flex flex-col justify-between border-2 border-teal-300">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                        <Icon className="h-8 w-8 text-teal-600" />
                      </div>
                      <h4 className="text-xl font-semibold text-gray-800 mb-4 flex flex-col items-center">
                        {Array.isArray(cardTitle) ? cardTitle.map((t) => <span key={t}>{t}</span>) : cardTitle}
                      </h4>
                      <p className="text-gray-600 mb-4 text-center">{desc}</p>
                    </div>
                    <button
                      onClick={() => setIsBookingOpen(true)}
                      className="mt-4 w-full px-6 py-2 bg-teal-600 text-white rounded-md hover:bg-yellow-300 hover:text-black transition-colors focus:outline-none focus:ring-2 focus:ring-teal-600"
                    >
                      Start Assessment
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* About the Counsellor */}
          <section aria-label="About the Counsellor" className="py-16 bg-white relative overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">About The Counsellor</h2>
                <div className="w-24 h-1 bg-teal-600 mx-auto"></div>
              </div>

              <div className="bg-teal-600 rounded-2xl shadow-xl p-8 max-w-4xl mx-auto relative">
                <div className="absolute -left-4 top-1/4 hidden lg:block">
                  <div className="bg-yellow-400 rounded-lg p-4 shadow-lg transform -rotate-6">
                    <p className="text-black font-bold text-lg ml-8">50+</p>
                    <p className="text-black flex items-center ml-2">
                      5 <Star className="text-yellow-600 mr-1 text-sm" /> Reviews
                    </p>
                  </div>
                </div>
                <div className="absolute -right-4 top-0.5 hidden lg:block">
                  <div className="bg-yellow-400 rounded-lg p-4 shadow-lg transform rotate-6">
                    <p className="text-black text-md">Let's find you a</p>
                    <p className="text-black text-lg font-bold ml-2">Perfect Fit!</p>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row items-center gap-8 mb-8">
                  <div className="flex flex-col items-center lg:w-1/3">
                    <div className="relative">
                      <img
                        src={ProfilePic}
                        alt="Subhransu Dutta - Career Counselling Expert"
                        loading="lazy"
                        className="w-40 h-40 rounded-full object-cover border-4 border-white shadow-lg"
                      />
                      <div className="absolute -bottom-2 -right-16 bg-white rounded-full p-2 shadow-lg">
                        <img
                          src="https://www.edumilestones.com/ccablack.png"
                          alt="Certification badge"
                          className="w-24 h-8"
                        />
                      </div>
                    </div>
                    <h4 className="text-2xl font-semibold text-white mt-7">Subhransu Dutta</h4>
                    <p className="text-yellow-300 font-medium">Career Counselling Expert</p>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      {['Career Guidance', 'Professional Mentoring', 'Student Counselling'].map((skill) => (
                        <span key={skill} className="bg-teal-700 text-white px-3 py-1 rounded-full text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="prose prose-lg prose-invert mt-12 lg:w-2/3 text-white">
                    <p className="mb-4">
                      To be able to guide myself to pursue a career as a counselor made me realize the value the right
                      resources have in a person's life.
                    </p>
                    <p className="mb-4">
                      Having 30 years of experience in the corporate universe and collecting knowledge about the lifestyle
                      over the years would not allow me to pass up the opportunity to guide people in the right direction.
                      Exposure to different leadership roles and tough decisions gave me the confidence to mentor
                      professionals at various levels throughout my corporate career.
                    </p>
                    <p>
                      With all the experience and responsibilities I have had in my life, I am ready to point in the right
                      direction and show students and professionals the path to success. "You only need guidance to lead
                      you in the right direction until you can do it yourself".
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                  {[
                    { title: 'Corporate Experience', value: '30+ Years' },
                    { title: 'Clients Guided', value: '100+' },
                    { title: 'Average Rating', value: '4.9/5' },
                  ].map((item) => (
                    <div key={item.title} className="bg-teal-700 rounded-lg p-4 text-center">
                      <p className="text-white font-bold text-2xl">{item.value}</p>
                      <p className="text-yellow-400 text-sm">{item.title}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <ReviewsCarousel />

          {/* FAQ Section */}
          <section aria-label="Frequently Asked Questions" className="py-16 bg-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-teal-50 rounded-full -mr-32 -mt-32 opacity-50"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-50 rounded-full -ml-32 -mb-32 opacity-50"></div>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Career Counselling in {city} — FAQs
                </h2>
                <div className="w-24 h-1 bg-teal-600 mx-auto mb-6"></div>
                <p className="text-gray-600 max-w-2xl mx-auto">
                  Answers to common questions about career counselling in {city}. Can't find what you're looking for?
                  Reach out directly.
                </p>
              </div>

              <div className="bg-teal-600 rounded-2xl shadow-xl overflow-hidden">
                {allFAQs.map((faq, index) => (
                  <FAQItem
                    key={index}
                    question={faq.question}
                    answer={faq.answer}
                    icon={faq.icon}
                    isOpen={index === openFAQIndex}
                    onClick={() => setOpenFAQIndex(index === openFAQIndex ? -1 : index)}
                  />
                ))}
              </div>

              <div className="text-center mt-12 space-y-6">
                <p className="text-gray-600">Still have questions?</p>
                <button
                  onClick={() => setIsBookingOpen(true)}
                  className="inline-flex items-center px-8 py-4 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors shadow-lg hover:shadow-xl space-x-2"
                >
                  <MessageCircle className="h-5 w-5" />
                  <span>Ask Us a Question</span>
                </button>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-teal-700">
            <div className="max-w-3xl mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Start Your Career Journey in {city}
              </h2>
              <p className="text-yellow-300 text-lg mb-8">{ctaNote} • First session is FREE</p>
              <button
                onClick={() => setIsBookingOpen(true)}
                className="px-10 py-4 bg-yellow-500 text-black font-bold text-lg rounded-md hover:bg-yellow-400 transition-colors shadow-lg"
              >
                Book Your Free Session
              </button>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer role="contentinfo" className="bg-gray-900 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div className="md:col-span-2">
                <div className="flex items-center mb-4">
                  <img src={Logo} alt="Nirvana Career Counselling Logo" className="h-12 w-12 mr-3" />
                  <h4 className="text-xl font-semibold">Nirvana Career Counselling</h4>
                </div>
                <p className="text-gray-300 mb-4 leading-relaxed">
                  Expert career guidance serving students and professionals across Jharkhand and Karnataka. Specialized
                  counselling for Jamshedpur's industrial sector, Ranchi's diverse opportunities, and Bangalore's tech
                  ecosystem.
                </p>
                <div className="flex items-center space-x-4">
                  <div className="bg-teal-600 rounded-full p-2">
                    <Phone className="h-4 w-4" />
                  </div>
                  <span className="text-gray-300">+91 9731166822</span>
                </div>
              </div>

              <div>
                <h5 className="text-lg font-semibold mb-4 text-teal-400">Service Areas</h5>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-teal-400" />
                    <span>Bangalore, Karnataka</span>
                  </li>
                  <li className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-teal-400" />
                    <span>Jamshedpur, Jharkhand</span>
                  </li>
                  <li className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-teal-400" />
                    <span>Ranchi, Jharkhand</span>
                  </li>
                  <li className="flex items-center">
                    <Globe className="h-4 w-4 mr-2 text-teal-400" />
                    <span>Online Consultations</span>
                  </li>
                </ul>
              </div>

              <div>
                <h5 className="text-lg font-semibold mb-4 text-teal-400">Quick Links</h5>
                <ul className="space-y-2 text-gray-300">
                  <li><Link to="/blog" className="hover:text-teal-400 transition-colors">Career Blog</Link></li>
                  <li><Link to="/jamshedpur" className="hover:text-teal-400 transition-colors">Jamshedpur Counselling</Link></li>
                  <li><Link to="/ranchi" className="hover:text-teal-400 transition-colors">Ranchi Counselling</Link></li>
                  <li>Psychometric Testing</li>
                  <li>Industry-Specific Advice</li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-6 text-center">
              <h4 className="text-xl font-semibold mb-2">Copyright © Nirvana Career Counselling</h4>
              <p className="text-gray-300 mb-2">📍 Proudly Serving Bangalore, Jamshedpur & Ranchi</p>
              <p className="text-sm text-gray-400">Jharkhand • Karnataka • Online Counselling Available Nationwide</p>
              <p className="mt-2 text-sm text-gray-400">
                🌟 Rated 4.9/5 • 🎓 30+ Years Experience • 🔒 100% Confidential
              </p>
            </div>
          </div>
        </footer>

        {/* Sticky Mobile CTA */}
        {isScrolled && (
          <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-teal-700 shadow-2xl border-t border-teal-500">
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <p className="text-white text-sm font-semibold">Book Your Free Session</p>
                <p className="text-yellow-300 text-xs">{city} • Online</p>
              </div>
              <button
                onClick={() => setIsBookingOpen(true)}
                className="bg-yellow-500 text-black font-bold px-4 py-2 rounded-md text-sm hover:bg-yellow-400"
              >
                Book Now →
              </button>
            </div>
          </div>
        )}

        {isBookingOpen && <BookingPopup onClose={() => setIsBookingOpen(false)} />}
      </div>
    </>
  );
};

export default CityLandingPage;
